import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Star, Send, Check } from "lucide-react";

export const FeedbackCard = ({ lookId }) => {
  const [existing, setExisting] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/looks/${lookId}/feedback`).then(({ data }) => {
      if (data) {
        setExisting(data);
        setRating(data.rating);
        setComment(data.comment || "");
      }
    }).catch(() => {});
  }, [lookId]);

  const submit = async () => {
    if (!rating) return toast.error("Pick a rating first");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/looks/${lookId}/feedback`, { rating, comment });
      setExisting(data);
      toast.success("Thank you — feedback saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="feedback-card" className="bg-[#1A1921] border border-white/10 p-8 mt-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0]">Your Feedback</span>
          <h3 className="font-serif text-2xl mt-2 font-light">How did this look feel?</h3>
        </div>
        {existing && (
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#A8A6A1]">
            <Check className="w-3 h-3 text-[#E3C2B0]" strokeWidth={1.5} /> Saved
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            data-testid={`feedback-star-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-7 h-7 ${
                n <= (hover || rating) ? "fill-[#E3C2B0] text-[#E3C2B0]" : "text-[#A8A6A1]/40"
              }`}
              strokeWidth={1.5}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-3 text-xs uppercase tracking-[0.3em] text-[#A8A6A1]">
            {["Poor", "Fair", "Good", "Lovely", "Adore it"][rating - 1]}
          </span>
        )}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What did you love? What could be better? (optional)"
        data-testid="feedback-comment"
        rows={3}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] text-base font-light resize-none placeholder:text-[#A8A6A1]/50"
      />

      <button
        onClick={submit}
        disabled={submitting || !rating}
        data-testid="feedback-submit"
        className="mt-6 inline-flex items-center gap-2 bg-[#E3C2B0] text-[#0A0908] px-6 py-3 rounded-sm hover:bg-[#F0D4C5] disabled:opacity-40 text-xs uppercase tracking-[0.3em]"
      >
        <Send className="w-3 h-3" strokeWidth={1.5} />
        {existing ? "Update" : "Send Feedback"}
      </button>
    </div>
  );
};
